import { useState } from "react";
import { Lock, Unlock, ShieldCheck, ShieldAlert } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function SealedVault({ quote, onUpdate, hseThreshold = 70 }) {
  const [unlocking, setUnlocking] = useState(false);
  const meets = (quote.hse_score ?? 0) >= hseThreshold;
  const sealed = !quote.hse_approved;

  const approve = async () => {
    if (!meets) {
      toast.error(`HSE score ${quote.hse_score} below required ${hseThreshold}`);
      return;
    }
    setUnlocking(true);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/approve-hse`, { approved: true });
      onUpdate?.(data);
      toast.success("Vault unsealed");
    } catch { toast.error("Could not approve"); }
    finally { setUnlocking(false); }
  };

  const lock = async () => {
    try {
      const { data } = await api.post(`/quotes/${quote.id}/approve-hse`, { approved: false });
      onUpdate?.(data);
    } catch {}
  };

  return (
    <div className="relative tray-inset p-5" data-testid={`sealed-vault-${quote.id}`}>
      {/* HSE status header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          {meets ? (
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
          ) : (
            <ShieldAlert className="w-5 h-5 text-red-400" />
          )}
          <div>
            <div className="font-mono-data text-[10px] tracking-widest uppercase text-text-secondary">HSE Safety Score</div>
            <div className="font-display text-2xl">{quote.hse_score ?? "—"}<span className="font-mono-data text-xs text-text-secondary"> / 100</span></div>
          </div>
        </div>
        <div className={`stamp ${meets ? "stamp-ok" : "stamp-bad"}`}>
          {meets ? "ELIGIBLE" : `BELOW ${hseThreshold}`}
        </div>
      </div>

      {/* Pricing display: sealed or revealed */}
      <div className="relative">
        <div className={sealed ? "vault-blur" : ""} data-testid={`vault-prices-${quote.id}`}>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-text-secondary">Total Quote</div>
              <div className="font-display text-3xl text-amber-500">
                ${(quote.total_amount || 0).toLocaleString()}
              </div>
            </div>
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-text-secondary">Currency / Units</div>
              <div className="font-mono-data text-base">{quote.currency} · {quote.unit_system}</div>
            </div>
          </div>
          <div className="font-mono-data text-xs text-text-secondary">
            {(quote.line_prices || []).length} line items priced
          </div>
        </div>

        {sealed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 flex flex-col items-center justify-center"
          >
            <Lock className="w-12 h-12 padlock-shadow text-amber-500 mb-3" />
            <div className="font-mono-data text-[10px] tracking-[0.25em] uppercase text-amber-500">
              Sealed Bid Vault
            </div>
            <div className="text-xs text-text-secondary mt-1 text-center max-w-xs">
              Approve HSE score to reveal vendor pricing
            </div>
          </motion.div>
        )}
      </div>

      <div className="mt-4 flex gap-2">
        {sealed ? (
          <button
            onClick={approve}
            disabled={!meets || unlocking}
            data-testid={`unseal-btn-${quote.id}`}
            className="btn-tactile btn-amber flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Unlock className="w-4 h-4" />
            {unlocking ? "Unsealing..." : "Approve HSE & Unseal"}
          </button>
        ) : (
          <button onClick={lock} data-testid={`reseal-btn-${quote.id}`} className="btn-tactile flex-1 inline-flex items-center justify-center gap-2">
            <Lock className="w-4 h-4" /> Re-seal
          </button>
        )}
      </div>
    </div>
  );
}
